import { faCheck, faExclamation } from '@fortawesome/free-solid-svg-icons';
export const isSingleBoundaryScale = (params) => {
  return params.type === 'low-normal' || params.type === 'normal-high';
};

export const isNegativePositiveScale = (params) => {
  return params.type === 'negative-positive';
};

export const trimToRange = (value, min, max) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

export const getScaleDetails = (params) => {

  let { value, boundary, lower_boundary, upper_boundary, result, decision_type } = params;

  // Convert string values to numbers
  value = Number.parseFloat(value);
  lower_boundary = Number.parseFloat(lower_boundary);
  upper_boundary = Number.parseFloat(upper_boundary);

  if (decision_type === 'female-manual') {
    lower_boundary = 2;
    upper_boundary = 4;
    value = 3;
    if (result?.toLowerCase() === 'abnormal_low') value = 1;
    else if (result?.toLowerCase() === 'abnormal_high') value = 5;
  }

  let barSize = 50;
  let leftBarSize = 0;

  // Handle 'negative-positive' marker (sexual health)
  if (isNegativePositiveScale(params)) {
    return {
      pointerPosition: result === 'green' ? 25 : 75,
      barSize: 50,
    };
  }

  // Handle single boundary scale
  if (isSingleBoundaryScale(params)) {
    const pointerPosition = (value / boundary / 2) * 100;
    return {
      pointerPosition: trimToRange(pointerPosition, 0, 100),
      barSize,
      leftBarSize,
    };
  }

  // For two-boundary scale (low-normal-high)
  let delta = upper_boundary - lower_boundary;
  let min = lower_boundary - delta;
  if (min < 0) min = 0;
  let max = upper_boundary + delta;

  leftBarSize = ((lower_boundary - min) / (max - min)) * 100;
  barSize = (100 - leftBarSize) / 2;
  const pointerPosition = ((value - min) / (max - min)) * 100;

  return {
    pointerPosition: trimToRange(pointerPosition, 2, 98),
    barSize,
    leftBarSize,
  };

  
};

// scaleUtils.js
export const renderRangeLabel = (decision_type, type, lower_boundary, upper_boundary, boundary) => {

  if (decision_type === 'female-manual') return null;

  if (type === 'low-normal-high') {
    if (!lower_boundary || !upper_boundary) {
      return `${lower_boundary} - ${upper_boundary}`
    } else {
      return `${lower_boundary.toFixed(2)} - ${upper_boundary.toFixed(2)}`
    }
  }

  if (type === 'low-normal') {
    return `${boundary}`;
  }

  if (type === 'normal-high') {
    return `${boundary}`;
  }

  return null;
};

export const getStatusIcon = (status) => {
  return ['good', 'normal'].includes(String(status)) ? faCheck : faExclamation;
};

/**
 * Removes the first line from content if it matches the status label
 */
export const removeStatusLineFromContent = (content, statusLabel) => {
  const lines = content.split('\n');
  if (lines[0].trim() === statusLabel || 
      lines[0].trim() === `${statusLabel}.` ||
      lines[0].trim().startsWith(statusLabel)) {
    const filteredLines = lines.slice(1);
    while (filteredLines.length > 0 && filteredLines[0].trim() === '') {
      filteredLines.shift();
    }
    return filteredLines.join('\n');
  }
  return content;
};

export const colorMap = {
  normal: '#D1E6E2',
  high:   '#D9808A',
  low:    '#E9CDCC',
  error:  '#facc15',
};
 
// ─────────────────────────────────────────────────────────────────────────────
// Graph utilities — used by MarkerGraph
// ─────────────────────────────────────────────────────────────────────────────
 
export const GRAPH_ZONE_COLORS = {
  low:  { barColor: '#E9CDCC', zoneFill: '#E9CDCC', dotColor: '#E9CDCC', textColor: '#003032', badgeBg: '#E9CDCC' },
  lowNormal:  { barColor: '#F0D88A', zoneFill: '#FEF3B0', dotColor: '#D4A017', textColor: '#003032', badgeBg: '#FEF9E0' },
  normal:     { barColor: '#D1E6E2', zoneFill: '#D1E6E266', dotColor: '#D1E6E2', textColor: '#003032', badgeBg: '#D1E6E2' },
  high:       { barColor: '#F4A8A8', zoneFill: '#D9808A99', dotColor: '#E85C5C', textColor: '#003032', badgeBg: '#D9808A99' },
  manual: { barColor: '#ECECEE', zoneFill: '#ECECEE', dotColor: '#ECECEE', textColor: '#003032', badgeBg: '#E9CDCC' }
};
 
/**
 * B12 boundary constants — used for the 4-zone B12 scale.
 */
const B12_LOW_MAX        = 37.5;
const B12_LOW_NORMAL_MAX = 70;
const B12_NORMAL_MAX     = 150;
const B12_Y_MAX          = 300;
 
/**
 * buildGraphConfig
 *
 * Derives zoneBands, yTicks, ranges, yMax, and getPointCategory
 * from a raw marker object.
 *
 * Supported marker types:
 *   'low-normal'        → Low / Normal          (single lower boundary)
 *   'normal-high'       → Normal / High         (single upper boundary)
 *   'low-normal-high'   → Low / Normal / High   (two boundaries)
 *   B12 (code === 'B12A' or decision_type === 'b12'):
 *                       → Low / Low-Normal / Normal / High (four zones)
 */
export const buildGraphConfig = (marker) => {
  const boundary      = parseFloat(marker.boundary)       || 0;
  const lowerBoundary = parseFloat(marker.lower_boundary) || 0;
  const upperBoundary = parseFloat(marker.upper_boundary) || 0;
  const type          = marker.type          || 'normal-high';
  const decisionType  = marker.decision_type || 'threshold';
  const code          = marker.code          || '';
 
  if(decisionType.includes("manual")){
    const padding = (169 - 83) * 0.6;
    const yMax    = parseFloat((169 + padding * 2.5).toFixed(2));
    const zoneBands = [
      { key: 'abnormalLow',    yLo: 0, yHi: 83, ...GRAPH_ZONE_COLORS.low    },
      { key: 'normal', yLo: 83, yHi: 169, ...GRAPH_ZONE_COLORS.normal },
      { key: 'abnormalHigh',   yLo: 169, yHi: yMax, ...GRAPH_ZONE_COLORS.high   },
    ];
    const yTicks = [,,, parseFloat((169 + padding).toFixed(2))];
    const ranges = [
      { key: 'abnormalLow',    label: 'Abnormal low',    min: 0,max: 10, ...GRAPH_ZONE_COLORS.low    },
      { key: 'normal', label: 'Normal', min: 5, max: 10, ...GRAPH_ZONE_COLORS.normal },
      { key: 'abnormalHigh',   label: 'Abnormal high',   min: 10, max: yMax, ...GRAPH_ZONE_COLORS.high   },
    ];
    const getPointCategory = (v) => {
      const resultStr = String(marker.result || '').toLowerCase();
      
      if (resultStr.includes('low')) {
        return { 
          key: 'abnormalLow',    
          label: 'Abnormal low',    
          ...GRAPH_ZONE_COLORS.low 
        };
      }
      
      if (resultStr.includes('high')) {
        return { 
          key: 'abnormalHigh',   
          label: 'Abnormal high',   
          ...GRAPH_ZONE_COLORS.high 
        };
      }
      
      // Default to normal
      return { 
        key: 'normal', 
        label: 'Normal', 
        ...GRAPH_ZONE_COLORS.normal 
      };
    };
    const getValuePositionInZone = (marker) => {
        const resultStr = String(marker.result || "").toLowerCase();

        if (resultStr.includes("low")) {
          return (0 + 83) / 2;
        }

        if (resultStr.includes("high")) {
          return (169 + yMax) / 2;
        }

        return (83 + 169) / 2;
      };
    
    return { zoneBands, yTicks, ranges, yMax, getPointCategory, getValuePositionInZone };
  }
  

  if (code === 'B12A' || decisionType === 'b12') {
    const yMax = B12_Y_MAX;
    const zoneBands = [
      { key: 'low',       yLo: 0,                yHi: B12_LOW_MAX,        ...GRAPH_ZONE_COLORS.low       },
      { key: 'lowNormal', yLo: B12_LOW_MAX,       yHi: B12_LOW_NORMAL_MAX, ...GRAPH_ZONE_COLORS.lowNormal },
      { key: 'normal',    yLo: B12_LOW_NORMAL_MAX, yHi: B12_NORMAL_MAX,    ...GRAPH_ZONE_COLORS.normal    },
      { key: 'high',      yLo: B12_NORMAL_MAX,    yHi: yMax,               ...GRAPH_ZONE_COLORS.high      },
    ];
    const yTicks = [0, B12_LOW_MAX, B12_LOW_NORMAL_MAX, B12_NORMAL_MAX, 200];
    const ranges = [
      { key: 'low',       label: 'Low',        min: 0,                max: B12_LOW_MAX,        ...GRAPH_ZONE_COLORS.low       },
      { key: 'lowNormal', label: 'Low-Normal', min: B12_LOW_MAX,       max: B12_LOW_NORMAL_MAX, ...GRAPH_ZONE_COLORS.lowNormal },
      { key: 'normal',    label: 'Normal',     min: B12_LOW_NORMAL_MAX, max: B12_NORMAL_MAX,    ...GRAPH_ZONE_COLORS.normal    },
      { key: 'high',      label: 'High',       min: B12_NORMAL_MAX,    max: yMax,               ...GRAPH_ZONE_COLORS.high      },
    ];
    const getPointCategory = (v) => {
      if (v < B12_LOW_MAX)        return { key: 'low',       label: 'Low',        ...GRAPH_ZONE_COLORS.low       };
      if (v < B12_LOW_NORMAL_MAX) return { key: 'lowNormal', label: 'Low-Normal', ...GRAPH_ZONE_COLORS.lowNormal };
      if (v <= B12_NORMAL_MAX)    return { key: 'normal',    label: 'Normal',     ...GRAPH_ZONE_COLORS.normal    };
      return                             { key: 'high',      label: 'High',       ...GRAPH_ZONE_COLORS.high      };
    };
    return { zoneBands, yTicks, ranges, yMax, getPointCategory };
  }
 
  // ── low-normal: value >= boundary = Normal, else Low ─────────────────────
  if (type === 'low-normal') {
    const yMax = boundary * 2.5;
    const zoneBands = [
      { key: 'low',    yLo: 0,        yHi: boundary, ...GRAPH_ZONE_COLORS.low    },
      { key: 'normal', yLo: boundary, yHi: yMax,     ...GRAPH_ZONE_COLORS.normal },
    ];
    const yTicks = [0, boundary, parseFloat((boundary * 2).toFixed(2))];
    const ranges = [
      { key: 'low',    label: 'Low',    min: 0,        max: boundary, ...GRAPH_ZONE_COLORS.low    },
      { key: 'normal', label: 'Normal', min: boundary, max: yMax,     ...GRAPH_ZONE_COLORS.normal },
    ];
    const getPointCategory = (v) =>
      v >= boundary ? { key: 'normal', label: 'Normal', ...GRAPH_ZONE_COLORS.normal }
                    : { key: 'low',    label: 'Low',    ...GRAPH_ZONE_COLORS.low    };
    return { zoneBands, yTicks, ranges, yMax, getPointCategory };
  }
 
  // ── normal-high: value <= boundary = Normal, else High ───────────────────
  if (type === 'normal-high' || (decisionType === 'threshold' && !lowerBoundary && !upperBoundary)) {
    const yMax = boundary * 2.5;
    const zoneBands = [
      { key: 'normal', yLo: 0,        yHi: boundary, ...GRAPH_ZONE_COLORS.normal },
      { key: 'high',   yLo: boundary, yHi: yMax,     ...GRAPH_ZONE_COLORS.high   },
    ];
    const yTicks = [0, boundary, parseFloat((boundary * 2).toFixed(2))];
    const ranges = [
      { key: 'normal', label: 'Normal', min: 0,        max: boundary, ...GRAPH_ZONE_COLORS.normal },
      { key: 'high',   label: 'High',   min: boundary, max: yMax,     ...GRAPH_ZONE_COLORS.high   },
    ];
    const getPointCategory = (v) =>
      v <= boundary ? { key: 'normal', label: 'Normal', ...GRAPH_ZONE_COLORS.normal }
                    : { key: 'high',   label: 'High',   ...GRAPH_ZONE_COLORS.high   };
    return { zoneBands, yTicks, ranges, yMax, getPointCategory };
  }
 
  // ── low-normal-high: two boundaries ──────────────────────────────────────
  if (type === 'low-normal-high') {
    const padding = (upperBoundary - lowerBoundary) * 0.6;
    const yMax    = parseFloat((upperBoundary + padding * 2.5).toFixed(2));
    const zoneBands = [
      { key: 'low',    yLo: 0,             yHi: lowerBoundary, ...GRAPH_ZONE_COLORS.low    },
      { key: 'normal', yLo: lowerBoundary, yHi: upperBoundary, ...GRAPH_ZONE_COLORS.normal },
      { key: 'high',   yLo: upperBoundary, yHi: yMax,          ...GRAPH_ZONE_COLORS.high   },
    ];
    const yTicks = [0, lowerBoundary, upperBoundary, parseFloat((upperBoundary + padding).toFixed(2))];
    const ranges = [
      { key: 'low',    label: 'Low',    min: 0,             max: lowerBoundary, ...GRAPH_ZONE_COLORS.low    },
      { key: 'normal', label: 'Normal', min: lowerBoundary, max: upperBoundary, ...GRAPH_ZONE_COLORS.normal },
      { key: 'high',   label: 'High',   min: upperBoundary, max: yMax,          ...GRAPH_ZONE_COLORS.high   },
    ];
    const getPointCategory = (v) => {
      if (v < lowerBoundary)  return { key: 'low',    label: 'Low',    ...GRAPH_ZONE_COLORS.low    };
      if (v <= upperBoundary) return { key: 'normal', label: 'Normal', ...GRAPH_ZONE_COLORS.normal };
      return                         { key: 'high',   label: 'High',   ...GRAPH_ZONE_COLORS.high   };
    };
    return { zoneBands, yTicks, ranges, yMax, getPointCategory };
  }
 
  // ── Fallback: treat as normal-high ────────────────────────────────────────
  const yMax = boundary ? boundary * 2.5 : 10;
  const bnd  = boundary || yMax / 2;
  const zoneBands = [
    { key: 'normal', yLo: 0,   yHi: bnd,  ...GRAPH_ZONE_COLORS.normal },
    { key: 'high',   yLo: bnd, yHi: yMax, ...GRAPH_ZONE_COLORS.high   },
  ];
  const yTicks = [0, bnd, parseFloat((bnd * 2).toFixed(2))];
  const ranges = [
    { key: 'normal', label: 'Normal', min: 0,   max: bnd,  ...GRAPH_ZONE_COLORS.normal },
    { key: 'high',   label: 'High',   min: bnd, max: yMax, ...GRAPH_ZONE_COLORS.high   },
  ];
  const getPointCategory = (v) =>
    v <= bnd ? { key: 'normal', label: 'Normal', ...GRAPH_ZONE_COLORS.normal }
             : { key: 'high',   label: 'High',   ...GRAPH_ZONE_COLORS.high   };
  return { zoneBands, yTicks, ranges, yMax, getPointCategory };
};
 
/**
 * buildLegendLabels
 * Builds human-readable legend strings from a ranges array.
 * e.g. "<37.5 (Low)", "37.5–70 (Low-Normal)", ">150 (High)"
 */
export const buildLegendLabels = (ranges, returnAsObject = false, isB12 = false) => {
  const result = ranges.map((r, idx) => {
    const prev = ranges[idx - 1];
    let prefix, suffix;
    
    if (!prev) {
      prefix = `<${r.max} `;
      suffix = `${r.label}`;
    } else if (idx === ranges.length - 1) {
      prefix = `>${prev.max} `;
      suffix = `${r.label}`;
    } else if (isB12 && r.label == "Normal")
    {
      prefix = `>${prev.max}–${r.max} `;
      suffix = `${r.label}`;
    }
     else {
      prefix = `${prev.max}–${r.max} `;
      suffix = `${r.label}`;
    }
    
    return returnAsObject ? { prefix, suffix } : `${prefix}${suffix}`;
  });
  
  return result;
};

/**
 * Calculate scale segments for Vitamin B12 marker
 * @param {number} value - The B12 value to plot
 * @returns {Object} Object containing segment sizes and boundary information
 */
export function calculateVitaminB12Scale(value) {
  const min = 0;
  const max = 200;

  const lowBoundary = 37.5;
  const lowNormalBoundary = 70;
  const normalBoundary = 150;

  // Calculate segment sizes as percentages of the scale
  const lowSize = ((lowBoundary - min) / (max - min)) * 100;
  const lowNormalSize = ((lowNormalBoundary - lowBoundary) / (max - min)) * 100;
  const normalSize = ((normalBoundary - lowNormalBoundary) / (max - min)) * 100;
  const highSize = 100 - (lowSize + lowNormalSize + normalSize);

  // Calculate pointer position
  let pointerPosition = ((value - min) / (max - min)) * 100;

  // Clamp pointer position to 0-100%
  if (pointerPosition < 0) pointerPosition = 0;
  if (pointerPosition > 100) pointerPosition = 100;

  return {
    lowSize,
    lowNormalSize,
    normalSize,
    highSize,
    pointerPosition,
    leftSize: lowSize, 
    lowBoundary,
    lowNormalBoundary,
    normalBoundary
  };
}

/**
 * Get scale segments based on marker code
 * Currently supports B12A. Can be extended for other markers.
 * @param {string} code - The marker code
 * @param {number} value - The marker value
 * @returns {Object|null} Scale segments or null if not applicable
 */
export function getScaleSegments(code, value) {
  if (code === "B12A") {
    return calculateVitaminB12Scale(value);
  }
  return null;
}

export function safeRenderValue(value) {
  if (value === null || value === undefined) {
    return "";
  }
  if (value instanceof Date) {
    return value.toString();
  }
  return value;
}